//@ts-nocheck
import React, { useEffect, useState } from "react";
import { DatePicker, Select, Skeleton, Tooltip as AntTooltip } from "antd";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  FiUsers, FiSend, FiClock, FiCheckCircle, FiPercent, FiDollarSign,
  FiAlertTriangle, FiPackage, FiShoppingCart, FiRefreshCw,
  FiBarChart2, FiTrendingUp, FiFileText, FiTarget, FiLayers, FiCalendar, FiChevronDown, FiMail, FiInfo,
} from "react-icons/fi";
import { getAteDashboardSummary } from "../../utils/API";
import CommonHeader from "../../Components/commonHeader";
import PageLogoutButton from "../../Components/PageLogoutButton";
import OtcTrackers from "./OtcTrackers";
import "./ate-dashboard.css";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const num = (v) => Number(v || 0);
const int = (v) => (num(v) === 0 ? "N/A" : num(v).toLocaleString("en-US"));
const usd = (v) => (num(v) === 0 ? "N/A" : `$${num(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const usd2 = (v) => (num(v) === 0 ? "N/A" : `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v, d = 2) => (num(v) === 0 ? "N/A" : `${num(v).toFixed(d)}%`);
const compact = (v) => Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(num(v));

// Where every number comes from, in plain words. Shown in the (i) tooltip next to each count.
const SRC = {
  gmPercent: "Gross margin divided by gross sales (OTC shipped orders) for the selected period.",
  revenue: "Total sales value of all orders placed in the selected period.",
  closeRate: "Won quotes divided by valid enquiries. A quote is won once a PO is received, it has shipped, or an order was placed against it. Spam, scam and job enquiries are not counted. The line below shows the same rate only for leads older than 90 days, which have had time to close. Close Rate is counted per lead cohort: leads received in the period, won to date. Recent periods will therefore keep rising, so it cannot be compared one to one with revenue in the same period.",
  units: "Total number of items on the orders placed in the selected period.",
  total: "All quotes received in the selected period, each counted once.",
  sent: "Quotes that have been sent to the customer.",
  pending: "Sent quotes that are still open: the customer is yet to decide or a PO is expected.",
  approved: "Sent quotes that turned into business: a PO was received, the order shipped, or an order was placed.",
  notSent: "Quotes received but not sent to the customer yet.",
  leads: "All enquiries received in the selected period, including spam, scam and job enquiries.",
  valid: "Total leads minus spam, scam and job enquiries.",
  qualified: "Valid enquiries that were quoted, are in conversation with the customer, or have reached the PO / shipping stage.",
  qualifiedRate: "Qualified leads as a percentage of valid enquiries.",
  missing: "Quotes that include at least one model we do not currently have in stock. Model names are compared ignoring capitals, spaces and punctuation. Each quote is counted once.",
  aov: "Total revenue divided by the number of orders in the selected period.",
  approvalShare: "Won quotes as a percentage of sent quotes.",
  chart: "Revenue is the total sales value and Gross Profit is the margin earned on it. COGS is the cost of the goods sold (Revenue minus Gross Profit). It is not marketing cost.",
};

const Info = ({ text }) => (
  <AntTooltip title={text} overlayStyle={{ maxWidth: 360 }}>
    <FiInfo className="ate-info" />
  </AntTooltip>
);

const AteDashboard: React.FC = () => {
  const [period, setPeriod] = useState("year");
  const [customRange, setCustomRange] = useState<[string, string] | null>(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (period === "custom" && !customRange) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getAteDashboardSummary(
      period === "custom"
        ? { period: "custom", startDate: customRange[0], endDate: customRange[1] }
        : { period },
    )
      .then((res) => { if (!cancelled) { setData(res); setFetchedAt(new Date()); } })
      .catch(() => { if (!cancelled) { setData(null); setError(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, customRange, reload]);

  const k = data?.kpis;
  const q = k?.quotations || {};

  // Quote flow: Total quotes -> Sent -> (of sent) Pending / Approved. Quotes not sent yet sit apart.
  const totalQuotes = num(q.total ?? num(q.pending) + num(q.approved));
  const sentCount = num(q.sent);
  const sentPending = num(q.sentPending);
  const sentApproved = num(q.sentApproved);
  const closedOther = Math.max(sentCount - sentPending - sentApproved, 0); // sent, then closed / lost
  const ofTotal = (v) => (totalQuotes ? (num(v) / totalQuotes) * 100 : 0);
  const ofSent = (v) => (sentCount ? (num(v) / sentCount) * 100 : 0);
  const sentShare = ofTotal(q.sent);
  const notSentShare = ofTotal(q.notSentYet);
  const approvedShare = ofSent(sentApproved); // Approval Share = approved among sent

  // One card per KPI. Revenue, Close Rate and Units Sold live in the hero cards;
  // Approved / Pending / Not sent live in the pipeline, so they are not repeated here.
  const qualified = k?.qualifiedLeadsCount ?? k?.qualifiedLeads;
  const kpiGroups = [
    {
      title: "Leads, Quotations & Orders",
      gridClass: "ate-grid-6",
      cards: k
        ? [
            { label: "Total Leads", src: SRC.leads, value: int(k.totalLeads), icon: <FiUsers />, color: "#004aad" },
            ...(k.validEnquiries != null
              ? [{ label: "Valid Enquiries", src: SRC.valid, value: int(k.validEnquiries), icon: <FiCheckCircle />, color: "#0284c7" }]
              : []),
            ...(qualified != null
              ? [{ label: "Qualified Leads", src: SRC.qualified, value: int(qualified), icon: <FiTarget />, color: "#16a34a" }]
              : []),
            ...(k.qualifiedLeadsRate != null
              ? [{ label: "Qualified Lead Rate", src: SRC.qualifiedRate, value: pct(k.qualifiedLeadsRate), icon: <FiPercent />, color: "#7c3aed" }]
              : []),
            { label: "No Model Availability", src: SRC.missing, value: int(k.quotesOnModelsWeDontHave), icon: <FiAlertTriangle />, color: "#dc2626" },
            { label: "Average Order Value", src: SRC.aov, value: usd2(k.averageOrderValue), icon: <FiShoppingCart />, color: "#4f46e5" },
          ]
        : [],
    },
  ];

  const statusData = k
    ? [
        { name: "Approved", value: sentApproved, color: "#10b981", share: ofTotal(sentApproved) },
        { name: "Pending", value: sentPending, color: "#f59e0b", share: ofTotal(sentPending) },
        { name: "Closed / other", value: closedOther, color: "#94a3b8", share: ofTotal(closedOther) },
        { name: "Not sent yet", value: num(q.notSentYet), color: "#38bdf8", share: notSentShare },
      ].filter((d) => d.value > 0)
    : [];

  const moneyData = k
    ? [
        { name: "Revenue", value: num(k.totalRevenue), color: "#004aad" },
        { name: "Gross Profit", value: num(k.grossProfit), color: "#10b981" },
        { name: "COGS", value: Math.max(num(k.totalRevenue) - num(k.grossProfit), 0), color: "#cbd5e1" },
      ]
    : [];

  return (
    <div className="ate-shell">
      <div className="ate-header">
        <CommonHeader className="mb-0" recordInputLabel="Records" rightContent={<PageLogoutButton />} />
      </div>

      <div className="ate-scroll">
        <div className="ate-wrap">
          {/* Dashboard Header (REMAINS EXACTLY AS IS) */}
          <div className="ate-head">
            <div className="ate-brand">
              <h1>ATE Dashboard</h1>
              <p>
                Sales performance overview
                {period === "all"
                  ? " · All time"
                  : data?.range?.start && data?.range?.end ? ` · ${data.range.start} → ${data.range.end}` : ""}
              </p>
            </div>
            <div className="ate-filters">
              {(data?.lastUpdated || fetchedAt) && (
                <span className="ate-updated">
                  Last updated: {new Date(data?.lastUpdated || fetchedAt).toLocaleString()}
                </span>
              )}
              <div className="ate-date-pill">
                <FiCalendar className="ate-date-icon" />
                <Select
                  value={period}
                  onChange={setPeriod}
                  className="ate-period-select"
                  bordered={false}
                  options={PERIODS}
                  suffixIcon={<FiChevronDown className="ate-arrow-icon" />}
                />
              </div>
              {period === "custom" && (
                <DatePicker.RangePicker onChange={(_, s) => setCustomRange(s[0] && s[1] ? [s[0], s[1]] : null)} />
              )}
              <button className="ate-refresh-btn" onClick={() => setReload((n) => n + 1)} title="Refresh">
                <FiRefreshCw className={loading ? "spin" : ""} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {data?.flags?.length > 0 && (
            <div className="ate-flags">
              <b>Data notes</b>
              <ul>{data.flags.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          )}

          {error && <div className="ate-error">Could not load dashboard data. Please try again.</div>}
          {period === "custom" && !customRange && !data && <div className="ate-empty">Pick a date range to load data.</div>}

          {/* OTC trackers merged in from the old Dashboard; the 3 hero cards sit beside Total Leads by Source */}
          <OtcTrackers
            period={period}
            customRange={customRange}
            allStart={data?.range?.start}
            aside={(gmPercent) => (
              <>
          {/* 3 Top Hero Metric Cards (REMAINS EXACTLY AS IS) */}
          <div className="ate-hero-cards ate-hero-side">
            {loading && !k ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div className="ate-hero-card" key={i}>
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                </div>
              ))
            ) : k ? (
              <>
                <div className="ate-hero-card">
                  <div className="ate-hero-info">
                    <span className="ate-hero-label">Gross Margin (GM) % <Info text={SRC.gmPercent} /></span>
                    <b className="ate-hero-val text-blue">{gmPercent ? `${gmPercent.toFixed(1)}%` : "N/A"}</b>
                    <span className="ate-hero-sub">Gross margin vs gross sales</span>
                  </div>
                  <div className="ate-hero-icon bg-blue-soft text-blue">
                    <FiTrendingUp />
                  </div>
                </div>

                <div className="ate-hero-card">
                  <div className="ate-hero-info">
                    <span className="ate-hero-label">Close Rate <Info text={SRC.closeRate} /></span>
                    <b className="ate-hero-val text-green">{pct(k.closeRate)}</b>
                    <span className="ate-hero-sub">
                      Leads older than 90 days: <b>{pct(k.matureCloseRate)}</b>
                    </span>
                  </div>
                  <div className="ate-hero-icon bg-green-soft text-green">
                    <FiPercent />
                  </div>
                </div>

                <div className="ate-hero-card">
                  <div className="ate-hero-info">
                    <span className="ate-hero-label">Units Sold <Info text={SRC.units} /></span>
                    <b className="ate-hero-val text-blue">{int(k.totalUnitsSold)}</b>
                    <span className="ate-hero-sub">Items sold in this period</span>
                  </div>
                  <div className="ate-hero-icon bg-blue-soft text-blue">
                    <FiPackage />
                  </div>
                </div>
              </>
            ) : null}
          </div>
              </>
            )}
          />

          {/* Key Performance Indicators */}
          <div className="ate-kpi-section">
            {loading && !k ? (
              <>
                <div className="ate-kpi-group">
                  <div className="ate-kpi-grid ate-grid-5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div className="ate-kpi-item" key={i}>
                        <Skeleton active paragraph={{ rows: 1 }} title={false} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ate-kpi-group">
                  <div className="ate-kpi-grid ate-grid-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div className="ate-kpi-item" key={i}>
                        <Skeleton active paragraph={{ rows: 1 }} title={false} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              kpiGroups.map((grp) => (
                <div className="ate-kpi-group" key={grp.title}>
                  <div className={`ate-kpi-grid ${grp.gridClass}`}>
                    {grp.cards.map((c) => (
                      <div className="ate-kpi-item" key={c.label}>
                        <div className="ate-kpi-circle-badge" style={{ background: c.color }}>
                          {c.icon}
                        </div>
                        <div className="ate-kpi-info">
                          <span className="ate-kpi-label">
                            {c.label.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="ate-kpi-label-end">{c.label.split(" ").slice(-1)[0]} <Info text={c.src} /></span>
                          </span>
                          <div className="ate-kpi-val">{c.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Bottom Analytics Section (3 panels) */}
          {k && (
            <div className="ate-charts">
              {/* 1. Performance at a Glance */}
              <div className="ate-panel">
                <div className="ate-panel-head">
                  <FiBarChart2 className="ate-panel-head-icon" />
                  <h3>Performance at a Glance</h3>
                </div>
                <div className="ate-glance-rows">
                  <div className="ate-glance-row">
                    <div className="ate-glance-top">
                      <div className="ate-glance-left">
                        <div className="ate-glance-icon-circle bg-green">
                          <FiCheckCircle />
                        </div>
                        <span className="ate-glance-title">Approval Share <Info text={SRC.approvalShare} /></span>
                      </div>
                      <b className="ate-glance-rate text-green">{pct(approvedShare, 1)}</b>
                    </div>
                    <div className="ate-glance-track">
                      <div style={{ width: `${Math.min(Math.max(approvedShare, 0), 100)}%`, background: "#16a34a" }} />
                    </div>
                    <small className="ate-glance-sub">Won quotes vs sent quotes</small>
                  </div>
                </div>
              </div>

              {/* 2. Revenue vs Profit (Bar chart with top labels) */}
              <div className="ate-panel">
                <div className="ate-panel-head">
                  <FiBarChart2 className="ate-panel-head-icon" />
                  <h3>Revenue vs Profit <Info text={SRC.chart} /></h3>
                </div>
                <div className="ate-chartbox">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={moneyData} margin={{ top: 28, left: 0, right: 10, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={14} tickLine={false} axisLine={false} />
                      <YAxis tickFormatter={(v) => `$${compact(v)}`} stroke="#94a3b8" fontSize={14} tickLine={false} axisLine={false} width={56} />
                      <Tooltip formatter={(v) => usd(v)} cursor={false} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={52}>
                        <LabelList
                          dataKey="value"
                          position="top"
                          formatter={(v) => `$${compact(v)}`}
                          fill="#0f172a"
                          fontSize={14}
                          fontWeight={800}
                          offset={8}
                        />
                        {moneyData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AteDashboard;
